import { memo } from 'react'

interface BlockRowProps {
  dayKey: number
  slotIndex: number
  /** Draw the day divider above this row. */
  startsDay: boolean
  /** This slot starts a clock hour, so the gutter shows a label. */
  startsHour: boolean
  hourLabel: string
  dayLabel: string
  isoDate: string
  isNow: boolean
  isSelected: boolean

  /** Fill colour, or null when the block is empty. */
  fillColor: string | null
  /** Contrast-corrected text colour for the fill. */
  textColor: string
  /** Shown only on the first row of a run. */
  label: string
  isRunStart: boolean
  isRunEnd: boolean
  hasComment: boolean
  isPlanned: boolean
  /** Plays the mark animation once. Cleared by the store shortly after. */
  isJustMarked: boolean
  /**
   * Colourblind texture for this fill, or null. A string rather than the
   * colour it derives from, so the hue maths runs once in the parent's memo
   * instead of on every row render.
   */
  pattern: string | null
}

/**
 * One timeline row: hour gutter plus block.
 *
 * The gutter lives inside the row rather than as a separate column, so label
 * and block cannot drift apart -- alignment is structural. It also means a day
 * of 46 or 50 rows needs no special handling here.
 *
 * EVERY PROP IS A PRIMITIVE, deliberately. `memo` compares shallowly, so
 * passing the object `slotAt()` returns would defeat it entirely: a fresh
 * object each render never matches, and every visible row would re-render on
 * every scroll frame. That matters more than usual because React Compiler
 * declines to memoize the parent (TanStack Virtual is not yet compatible).
 *
 * Runs are expressed as flags rather than merged elements: only the first row
 * of a run draws its label, and interior edges are suppressed, so three hours
 * of Sleep reads as one block instead of six stamped repetitions.
 */
function BlockRowImpl({
  dayKey,
  slotIndex,
  startsDay,
  startsHour,
  hourLabel,
  dayLabel,
  isoDate,
  isNow,
  isSelected,
  fillColor,
  textColor,
  label,
  isRunStart,
  isRunEnd,
  hasComment,
  isPlanned,
  isJustMarked,
  pattern,
}: BlockRowProps) {
  const classes = [
    'tl-block',
    startsHour && !fillColor ? 'tl-block--hour' : '',
    fillColor ? 'tl-block--filled' : 'tl-block--empty',
    isRunStart ? 'tl-block--run-start' : '',
    isRunEnd ? 'tl-block--run-end' : '',
    isSelected ? 'tl-block--selected' : '',
    isPlanned ? 'tl-block--planned' : '',
    isJustMarked ? 'tl-block--just-marked' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const description = fillColor
    ? `${hourLabel} on ${isoDate}, ${label || 'filled'}${hasComment ? ', has comment' : ''}${isPlanned ? ', planned' : ''}`
    : `${hourLabel} on ${isoDate}, empty`

  return (
    <>
      {startsDay ? (
        <div className="tl-divider">
          <span className="tl-divider-label">{dayLabel}</span>
          <span className="tl-divider-rule" aria-hidden="true" />
        </div>
      ) : null}

      <div className={`tl-row${isNow ? ' tl-row--now' : ''}`}>
        <div className="tl-gutter" aria-hidden="true">
          {startsHour ? <span className="tl-hour">{hourLabel}</span> : null}
        </div>

        {/* A div, not a button: selection is owned by the gesture layer on the
            scroll container, which needs to distinguish a tap from a scroll and
            from a drag. A button would swallow the pointer sequence and fire on
            taps that were meant to be scrolls. */}
        <div
          className={classes}
          style={
            fillColor
              ? ({
                  background: fillColor,
                  color: textColor,
                  // Read by the colourblind rule in themes.css. Always set, so
                  // toggling the mode is a pure CSS switch with no re-render.
                  ['--block-pattern' as string]: pattern ?? 'none',
                } as React.CSSProperties)
              : undefined
          }
          role="gridcell"
          aria-label={description}
          aria-selected={isSelected}
          data-day={dayKey}
          data-slot={slotIndex}
        >
          {isRunStart && label ? <span className="tl-block-label">{label}</span> : null}
          {hasComment ? (
            <span className="tl-block-comment" aria-hidden="true">
              &bull;
            </span>
          ) : null}
        </div>
      </div>
    </>
  )
}

export const BlockRow = memo(BlockRowImpl)
